import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, of, shareReplay, tap } from 'rxjs';

import { API } from '@constants/api.constants';
import { STORAGE } from '@constants/storage.constant';
import { environment } from '@environment/environment';
import { APIResponseModel } from '@models/common.model';
import { UserConfig } from '@models/user-config.model';
import { StorageService } from '@services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class UserConfigService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);

  private readonly config = signal<UserConfig | null>(
    this.storageService.getFromLocal(STORAGE.USER_CONFIG)
  );

  private load$: Observable<APIResponseModel<UserConfig>> | null = null;

  /** Logo URL for whitelabeling. Falls back to environment default when not configured. */
  readonly logoUrl = computed(
    () => this.config()?.logo_image?.trim() || environment.logo
  );

  /** Verifier URL for credential verification. Falls back to environment default when not configured. */
  readonly verifierUrl = computed(() => {
    const base =
      this.config()?.verifier_url?.trim() || environment.verifierUrl;
    return base.endsWith('/') ? base : `${base}/`;
  });

  /** Theme preference (light/dark). */
  readonly theme = computed(() => this.config()?.theme ?? 'light');

  /** Whether DigiLocker features (e.g. dashboard tab) are enabled for the user. */
  readonly digilockerEnabled = computed(
    () => this.config()?.digilocker_enable === true
  );

  /** Full user config for advanced use cases. */
  readonly configSignal = () => this.config;

  getConfig(): UserConfig | null {
    return this.config();
  }

  /**
   * Loads user config from API. Uses shareReplay to cache the result and avoid duplicate requests.
   * @param forceRefresh When true, bypasses cache and fetches fresh data.
   */
  loadConfig(forceRefresh = false): Observable<APIResponseModel<UserConfig>> {
    if (this.load$ && !forceRefresh) {
      return this.load$;
    }

    this.load$ = this.http
      .get<APIResponseModel<UserConfig>>(API.USER_CONFIG)
      .pipe(
        tap((response) => {
          if (response?.data) {
            this.config.set(response.data);
            this.storageService.setToLocal(STORAGE.USER_CONFIG, response.data);
          }
        }),
        catchError(() => {
          return of({
            status: false,
            statusCode: 500,
            message: 'Failed to load user config',
            data: null as unknown as UserConfig,
            error: null
          });
        }),
        shareReplay(1)
      );

    return this.load$;
  }

  /**
   * Clears user config from memory and storage. Call on logout.
   */
  clearConfig(): void {
    this.config.set(null);
    this.load$ = null;
    this.storageService.removeFromLocal(STORAGE.USER_CONFIG);
  }
}
